<template>
    <ReleaseAnnouncementModal @confirm="dismissModal(PaidTariffModalType.ReleaseAnnouncement)" @hidden="dismissModal(PaidTariffModalType.ReleaseAnnouncement)" ref="releaseAnnouncementModal" v-if="freeAccessUntil" :free-access-until="freeAccessUntil" :is-trial-expired="isTrialExpired"></ReleaseAnnouncementModal>
    <ProExpiringModal :payment-failed="false" @details="onDetails" @confirm="onConfirmProExpiring" @hidden="dismissModal(PaidTariffModalType.ProExpiring)" ref="proExpiringModal" v-if="proExpiresAt" :pro-expires-at="proExpiresAt"></ProExpiringModal>
    <ProExpiringModal :payment-failed="true" @details="onDetails"  @confirm="onConfirmProPaymentFailed" @hidden="dismissModal(PaidTariffModalType.ProPaymentFailed)" ref="paymentFailedModal" v-if="proExpiresAt" :pro-expires-at="proExpiresAt"></ProExpiringModal>
    <TrialExpiringModal ref="trialExpiringModal" @confirm="onConfirm(PaidTariffModalType.TrialExpiring)" @hidden="dismissModal(PaidTariffModalType.TrialExpiring)" v-if="trialDaysRemaining != null" />
    <PauseProModal ref="pauseProModal" v-if="pauseFeature" :feature="pauseFeature" :pause-return-deadline="pauseReturnDeadline" :show-navigate-on-close="pauseShowNavigate" @resume="onPauseResume" @cancel="onPauseCancel" @hidden="onPauseHidden" />
    <PauseEndingSoonModal ref="pauseEndingSoonModal" v-if="pauseEndsAt" :pause-ends-at="pauseEndsAt" @confirm="onPauseEndingSoonAction" @secondary="onPauseEndingSoonAction" @hidden="onPauseEndingSoonHidden" />
    <PauseResumedModal ref="pauseResumedModal" @hidden="dismissModal(PaidTariffModalType.PauseResumed)" />
    <PauseFinalChanceModal ref="pauseFinalChanceModal" v-if="transferToFreeAt" :transfer-to-free-at="transferToFreeAt" @confirm="onPauseFinalChanceAction" @secondary="onPauseFinalChanceAction" @hidden="onPauseFinalChanceHidden" />
    <ResumeImportantModal ref="resumeImportantModal" :has-active-paid-period="true" @resume="onResumeConfirm" @cancel="navigateBackOrHome" />
    <TryTrialModal @hidden="dismissModal(PaidTariffModalType.TryTrial)" @confirm="onConfirmTryTrial" ref="tryTrialModal" />
    <TrialExpiredModal ref="trialExpiredModal" @confirm="onConfirmTrialExpired" @hidden="dismissModal(PaidTariffModalType.TrialExpired)" v-if="modalType === PaidTariffModalType.TrialExpired" />
    <UpsellAfterCancelModals v-if="otherEntryModalsAllowed" />
    <PackagesAnnouncementModals v-if="otherEntryModalsAllowed" />
    <ServiceOutageModals @checked="onServiceOutageChecked" />
    <ServiceRecoveryModals v-if="serviceRecoveryAllowed" @checked="onServiceRecoveryChecked" />
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, nextTick } from 'vue';
import ReleaseAnnouncementModal from './components/ReleaseAnnouncementModal.vue';
import { PaidFeaturesApi, PaidTariffModalType } from '@/api/PaidFeaturesApi';
import ProExpiringModal from './components/ProExpiringModal.vue';
import TrialExpiringModal from './components/TrialExpiringModal.vue';
import PauseEndingSoonModal from './components/PauseEndingSoonModal.vue';
import PauseResumedModal from './components/PauseResumedModal.vue';
import PauseFinalChanceModal from './components/PauseFinalChanceModal.vue';
import PauseProModal from './components/PauseProModal.vue';
import ResumeImportantModal from '@/pages/Subscription/components/ResumeImportantModal.vue';
import TrialExpiredModal from './components/TrialExpiredModal.vue';
import { UsersApi } from '@/api/UsersApi';
import { TeacherApi } from '@/api/TeacherApi';
import { UserInfoModel } from '@/types/UserInfoModel';
import { SubscriptionApi } from '@/api/SubscriptionApi';
import { useToast } from 'vue-toastification';
import { PaymentsApi } from '@/api/PaymentsApi';
import TryTrialModal from './components/TryTrialModal.vue';
import UpsellAfterCancelModals from '../UpsellAfterCancelModals/UpsellAfterCancelModals.vue';
import PackagesAnnouncementModals from '../PackagesAnnouncementModals/PackagesAnnouncementModals.vue';
import ServiceOutageModals from '../ServiceOutageModals/ServiceOutageModals.vue';
import ServiceRecoveryModals from '../ServiceRecoveryModals/ServiceRecoveryModals.vue';

type PauseFeature = 'calls' | 'board' | 'students';

interface PauseProModalShowDetail {
    feature: PauseFeature;
    pauseReturnDeadline: string | null;
    isEmbedded?: boolean;
}

const toast = useToast();

const isPaymentProcess = ref(false);
const modalType = ref<PaidTariffModalType | null>(null);
const proExpiresAt = ref<null | string>(null);
const freeAccessUntil = ref<null | string>(null);
const isTrialExpired = ref(false);
const trialDaysRemaining = ref<null | number>(null);
const pauseEndsAt = ref<null | string>(null);
const transferToFreeAt = ref<null | string>(null);
const pauseFeature = ref<PauseFeature | null>(null);
const pauseReturnDeadline = ref<string | null>(null);
const isPauseResumeRequested = ref(false);
const pauseShowNavigate = ref(true);
const pauseIsEmbedded = ref(false);

// Модалка о сбое (FINCH-2630) важнее остальных модалок при входе: тарифные, апселл и анонс
// пакетов запрашиваются, только когда её проверка завершилась и показывать её не нужно.
// Если она открыта — остальные придут на следующей загрузке страницы после её закрытия,
// а не стопкой поверх. Действует в пределах одной страницы: во вложенном фрейме (доска
// внутри звонка) модалка о сбое пропускается, и остальные модалки там показываются как раньше.
const otherEntryModalsAllowed = ref(false);

// Модалка «Восстанавливаем работу Finch» (FINCH-2631) — следующая в цепочке после модалки о сбое:
// её хост монтируется после checked(false) от модалки о сбое, а остальные модалки входа ждут уже её checked(false).
const serviceRecoveryAllowed = ref(false);

const releaseAnnouncementModal = ref<InstanceType<typeof ReleaseAnnouncementModal> | null>(null);
const proExpiringModal = ref<InstanceType<typeof ProExpiringModal> | null>(null);
const paymentFailedModal = ref<InstanceType<typeof ProExpiringModal> | null>(null);
const trialExpiringModal = ref<InstanceType<typeof TrialExpiringModal> | null>(null);
const pauseProModal = ref<InstanceType<typeof PauseProModal> | null>(null);
const pauseEndingSoonModal = ref<InstanceType<typeof PauseEndingSoonModal> | null>(null);
const pauseResumedModal = ref<InstanceType<typeof PauseResumedModal> | null>(null);
const pauseFinalChanceModal = ref<InstanceType<typeof PauseFinalChanceModal> | null>(null);
const resumeImportantModal = ref<InstanceType<typeof ResumeImportantModal> | null>(null);

function onPauseProShow(event: Event) {
    const customEvent = event as CustomEvent<PauseProModalShowDetail>;
    pauseFeature.value = customEvent.detail.feature;
    pauseReturnDeadline.value = customEvent.detail.pauseReturnDeadline;
    isPauseResumeRequested.value = false;
    
    // Determine if we should navigate on close:
    // - Don't navigate if it's StudentCards
    // - Don't navigate if it's embedded board (in iframe)
    // - Do navigate for calls and standalone board
    const isEmbeddedBoard = customEvent.detail.isEmbedded === true;
    pauseIsEmbedded.value = isEmbeddedBoard;
    pauseShowNavigate.value = customEvent.detail.feature !== 'students' && !isEmbeddedBoard;
    
    nextTick(() => {
        pauseProModal.value?.show();
    });
}

function onPauseCancel() {
    isPauseResumeRequested.value = false;
    pauseProModal.value?.hide();
}

function onPauseResume() {
    isPauseResumeRequested.value = true;
    pauseProModal.value?.hide();
}

function navigateBackOrHome() {
    if (window.history.length > 1) {
        window.history.back();
        return;
    }

    window.location.href = '/';
}

function onPauseHidden() {
    if (isPauseResumeRequested.value) {
        isPauseResumeRequested.value = false;
        nextTick(() => {
            resumeImportantModal.value?.show();
        });
        return;
    }

    if (pauseShowNavigate.value) {
        navigateBackOrHome();
        return;
    }

    // Embedded board inside call: close board instead of navigation.
    if (pauseFeature.value === 'board' && pauseIsEmbedded.value) {
        window.postMessage({ type: 'close-board' }, '*');
    }
}

function getPauseFlag(entity: unknown): boolean {
    const data = entity as { isOnPause?: boolean; IsOnPause?: boolean };
    return Boolean(data?.isOnPause ?? data?.IsOnPause ?? false);
}

function hasStudentsAccess(entity: unknown): boolean {
    const data = entity as {
        hasProTariff?: boolean;
        hasUnlimitedStudents?: boolean;
        studentCardsCount?: number | null;
        baseTariffMaxStudentCount?: number | null;
    };

    if (data?.hasProTariff || data?.hasUnlimitedStudents) {
        return true;
    }

    return Number(data?.studentCardsCount) < Number(data?.baseTariffMaxStudentCount);
}

function getPauseDeadline(entity: unknown): string | null {
    const data = entity as { pauseReturnDeadline?: string | null; PauseReturnDeadline?: string | null };
    return data?.pauseReturnDeadline ?? data?.PauseReturnDeadline ?? null;
}

function showStudentLimitModal() {
    const limitModal = document.getElementById('StudentLimitWarningModal');
    if (limitModal) {
        const bootstrapWindow = window as Window & {
            bootstrap?: {
                Modal: new (element: Element) => { show: () => void };
            };
        };
        bootstrapWindow.bootstrap?.Modal && new bootstrapWindow.bootstrap.Modal(limitModal).show();
    }
}

function interceptLegacyStudentLimitModal(event: Event) {
    const target = event.target;
    const element = target instanceof Element ? target : target instanceof Node ? target.parentElement : null;
    const trigger = element?.closest('[data-bs-target="#StudentLimitWarningModal"], [data-bs-target="#StudentAlmostLimitWarningModal"]');

    if (!trigger) {
        return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    new TeacherApi().getCurrentTeacherTariffInfo()
        .then((result) => {
            if (result.isSuccess && !hasStudentsAccess(result.entity) && getPauseFlag(result.entity)) {
                window.dispatchEvent(new CustomEvent('pause-pro-modal:show', {
                    detail: {
                        feature: 'students',
                        pauseReturnDeadline: getPauseDeadline(result.entity),
                    },
                }));
                return;
            }

            showStudentLimitModal();
        })
        .catch((e) => {
            console.error(e);
            showStudentLimitModal();
        });
}

async function onResumeConfirm() {
    let userData: UserInfoModel | null = null;
    try {
        const result = await new UsersApi().getCurrentInfo();
        if (result.isSuccess) {
            userData = result.entity;
        }
    } catch (e) {
        console.error(e);
    }

    if (!userData) {
        return;
    }

    try {
        const result = await new SubscriptionApi().resume(userData.id);
        if (result.isSuccess) {
            window.location.reload();
        } else {
            throw errorsListToString(result.errors);
        }
    } catch (e) {
        console.error(e);
        navigateBackOrHome();
    }
}

function errorsListToString(errors: { key: string; value: string }[]): string {
    return errors.map(err => `${err.key}: ${err.value}`).join('\n');
}
const tryTrialModal = ref<InstanceType<typeof TryTrialModal> | null>(null);
const trialExpiredModal = ref<InstanceType<typeof TrialExpiredModal> | null>(null);

const dismissed = new Set<PaidTariffModalType>();

async function dismissModal(type: PaidTariffModalType) {
    if (dismissed.has(type)) return;
    dismissed.add(type);
    try {
        return await new PaidFeaturesApi().dismissNewTariffModal(type);
    } catch (e) {
        console.error(e);
    }
}

async function onDetails() {
    await dismissModal(PaidTariffModalType.ProExpiring);
    window.location.href = '/Subscription';
}

async function onConfirmTryTrial() {
    await dismissModal(PaidTariffModalType.TryTrial);
    window.location.href = '/PaidFeatures';
}

async function onConfirmTrialExpired() {
    await dismissModal(PaidTariffModalType.TrialExpired);
    window.location.href = '/PaidFeatures';
}

async function onConfirmProExpiring() {
    await dismissModal(PaidTariffModalType.ProExpiring);
    let userData: UserInfoModel | null = null;
    try {
        const result = await new UsersApi().getCurrentInfo();
        if (result.isSuccess) {
            userData = result.entity;
        }
        if (!userData) {
            throw new Error('userData is not defined');
        }
    } catch (e) {
        console.error(e);
        return;
    }
    try {
        const result = await new SubscriptionApi().restore(userData.id);
        if (result.isSuccess) {
            window.location.href = '/Subscription';
        } else {
            console.error(result.errors);
        }
    } catch(e) {
        console.error(e);
    }
}

async function retryPayment(userData: UserInfoModel): Promise<number | null> {
    if (!userData) {
        return null;
    }
    const result = await new PaymentsApi().retryPayment(userData?.id);
    if (result.isSuccess) {
        return result.entity;
    } else {
        toast.error(result.errors.map(err => `${err.key}: ${err.value}`).join('\n'));
        return null;
    }
}

async function onConfirmProPaymentFailed() {
    await dismissModal(PaidTariffModalType.ProPaymentFailed);
    let userData: UserInfoModel | null = null;
    try {
        const result = await new UsersApi().getCurrentInfo();
        if (result.isSuccess) {
            userData = result.entity;
        }
        if (!userData) {
            throw new Error('userData is not defined');
        }
    } catch (e) {
        console.error(e);
        navigateBackOrHome();
        return;
    }
    isPaymentProcess.value = true;
    const paymentId = await retryPayment(userData);
    if (!paymentId) {
        isPaymentProcess.value = false;
        navigateBackOrHome();
        return;
    }
    // Списание прошло синхронно; продление PRO подтвердит вебхук (~1с) — обновляем страницу.
    setTimeout(() => window.location.reload(), 2000);
}

async function onConfirm(type: PaidTariffModalType) {
    await dismissModal(type);
    window.location.href = '/Subscription';
}

// Все CTA групп 1 и 3 (оплатить/отменить/подробнее) ведут в раздел «Подписка».
// Гасим именно показанный повод (за 7д/1д; день окончания/конец grace) — тип пришёл с бэка в modalType.
async function onPauseEndingSoonAction() {
    if (modalType.value != null) {
        await dismissModal(modalType.value);
    }
    window.location.href = '/Subscription';
}

async function onPauseFinalChanceAction() {
    if (modalType.value != null) {
        await dismissModal(modalType.value);
    }
    window.location.href = '/Subscription';
}

// Закрытие без CTA (крестик) — помечаем показанный повод, но без перехода.
async function onPauseEndingSoonHidden() {
    if (modalType.value != null) {
        await dismissModal(modalType.value);
    }
}

async function onPauseFinalChanceHidden() {
    if (modalType.value != null) {
        await dismissModal(modalType.value);
    }
}

function onServiceOutageChecked(shown: boolean) {
    if (shown) {
        return;
    }
    serviceRecoveryAllowed.value = true;
}

async function onServiceRecoveryChecked(shown: boolean) {
    if (shown) {
        return;
    }
    otherEntryModalsAllowed.value = true;
    await showPaidTariffModal();
}

onMounted(() => {
    // Прослушка события с доски для показа модалки паузы ПРО при попытке подключить студентов сверх лимита на базовом тарифе или при попытке подключить доску внутри другого приложения (например, внутри звонка).
    window.addEventListener('pause-pro-modal:show', onPauseProShow as EventListener);
    document.addEventListener('click', interceptLegacyStudentLimitModal, true);
});

async function showPaidTariffModal() {
    const modalInfo = await new PaidFeaturesApi().getModalInfo();

    if (!modalInfo.entity?.shouldShow || !modalInfo.entity.modalType) {
        return;
    }

    modalType.value = modalInfo.entity.modalType;

    if (modalInfo.entity.proExpiresAt) {
        proExpiresAt.value = modalInfo.entity.proExpiresAt;
    }
    if (modalInfo.entity.freeAccessUntil) {
        freeAccessUntil.value = modalInfo.entity.freeAccessUntil;
    }
    if (modalInfo.entity.isTrialExpired) {
        isTrialExpired.value = modalInfo.entity.isTrialExpired;
    }
    if (modalInfo.entity.trialDaysRemaining) {
        trialDaysRemaining.value = modalInfo.entity.trialDaysRemaining;
    }
    if (modalInfo.entity.pauseEndsAt) {
        pauseEndsAt.value = modalInfo.entity.pauseEndsAt;
    }
    if (modalInfo.entity.transferToFreeAt) {
        transferToFreeAt.value = modalInfo.entity.transferToFreeAt;
    }

    await nextTick();

    if (modalType.value === PaidTariffModalType.ReleaseAnnouncement) {
        releaseAnnouncementModal.value?.show();
    } else if (modalType.value === PaidTariffModalType.ProExpiring) {
        proExpiringModal.value?.show();
    } else if (modalType.value === PaidTariffModalType.ProPaymentFailed) {
        paymentFailedModal.value?.show();
    } else if (modalType.value === PaidTariffModalType.TrialExpiring) {
        trialExpiringModal.value?.show();
    } else if (modalType.value === PaidTariffModalType.TryTrial) {
        tryTrialModal.value?.show();
    } else if (modalType.value === PaidTariffModalType.TrialExpired) {
        trialExpiredModal.value?.show();
    } else if (modalType.value === PaidTariffModalType.PauseEndingSoon
        || modalType.value === PaidTariffModalType.PauseEndingSoonLastDay) {
        pauseEndingSoonModal.value?.show();
    } else if (modalType.value === PaidTariffModalType.PauseResumed) {
        pauseResumedModal.value?.show();
    } else if (modalType.value === PaidTariffModalType.PauseFinalChance
        || modalType.value === PaidTariffModalType.PauseGraceEnding) {
        pauseFinalChanceModal.value?.show();
    }
}

onBeforeUnmount(() => {
    window.removeEventListener('pause-pro-modal:show', onPauseProShow as EventListener);
    document.removeEventListener('click', interceptLegacyStudentLimitModal, true);
});
</script>
